import { useEffect, useState } from 'react'

import { Location, useLocation, useNavigate } from 'react-router-dom'

import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { isTextBlockConfig, QuoteBlock } from '@sequencehq/quote-content'

import { QuoteTemplatePickerModal } from 'common/modals/quotes/quoteTemplatePicker.modal'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useApplyQuoteTemplate } from 'Cube/view/layouts/quote/hooks/useApplyQuoteTemplate'

import { AUTO_DISPLAY_QUOTE_TEMPLATE_MODAL_STATE } from './constants'

type LocationState = typeof AUTO_DISPLAY_QUOTE_TEMPLATE_MODAL_STATE

/**
 * If the quote only has empty paragraphs, consider the quote to be empty.
 * Needed because the initial save on a fresh quote contains an empty paragraph
 * inserted by Blocknote.
 */
const hasNoContent = (blocks: QuoteBlock[]) =>
  blocks.every(
    block =>
      block.blockType === 'paragraph' &&
      isTextBlockConfig(block.config) &&
      block.config.text.length === 0
  )

/**
 * Displays the "Apply quote template" modal when `AUTO_DISPLAY_QUOTE_TEMPLATE_MODAL_STATE`
 * is passed via react-router state. Currently used to offer users following a create quote
 * link from the Hubspot V2 integration
 */
export const AutoDisplayQuoteTemplateModal = () => {
  const location: Location<LocationState> = useLocation()
  const navigate = useNavigate()

  const ctx = useCubeContext()
  const applyQuoteTemplate = useApplyQuoteTemplate()

  const [enabled, setEnabled] = useState(
    () =>
      !!location.state?.showQuoteTemplateModal &&
      hasNoContent(ctx.queries.initialData.presentation.blocks)
  )

  // Remove state to prevent retrigger after refresh or navigating back
  useEffect(() => {
    if (!location.state?.showQuoteTemplateModal) return

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null
    })
  }, [
    location.state?.showQuoteTemplateModal,
    location.pathname,
    location.search,
    navigate
  ])

  const templatesQuery = useQuery(
    dashboard20240730Client.getListQuoteTemplates,
    undefined,
    { enabled, select: res => res?.items ?? [] }
  )

  if (!enabled || !applyQuoteTemplate.visible || !templatesQuery.data?.length) {
    return null
  }

  return (
    <QuoteTemplatePickerModal
      showOverwriteWarning={false}
      onClose={() => setEnabled(false)}
      onSelect={applyQuoteTemplate.onSelection}
    />
  )
}
